import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PsfColors, buildPsfColors } from '../theme';
import StartWizard, { PsfMode } from '../components/StartWizard';
import CompareView from '../components/CompareView';
import BenchmarkView from '../components/BenchmarkView';

// Re-exported for backwards compatibility with earlier imports.
export { buildPsfColors };
export type { PsfColors };

type Stage = 'wizard' | 'compare' | 'benchmark';

export interface PsfReportContext {
  userInfo: { Name?: string; Mobile?: string; Email?: string; CEANumber?: string; Photo?: string } | null;
  consumerName?: string | null;
}

/**
 * Personalized PSF flow:
 *   Goal (Compare | Benchmark with New Launch)
 *     → Compare: raw transactions browser (unit/project views + filters)
 *     → Benchmark: scroll journey (tenure → market → budget → location →
 *       amenities → building) that lands on the adjustable project table
 * "Start Over" returns to the goal picker from anywhere.
 */
export default function PsfDashboard({ reportContext }: { reportContext?: PsfReportContext }) {
  const muiTheme = useTheme();
  const C = buildPsfColors(muiTheme.palette.mode as 'dark' | 'light', muiTheme.palette);

  const [stage, setStage] = useState<Stage>('wizard');
  // Remount key so Benchmark state (adjustments/filters) resets on re-entry.
  const [sessionKey, setSessionKey] = useState(0);

  const reset = () => {
    setStage('wizard');
    setSessionKey((k) => k + 1);
  };


  return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: C.surface, color: C.text }}>
      {stage === 'wizard' && (
        <StartWizard onPickMode={(mode: PsfMode) => setStage(mode)} c={C} />
      )}
      {stage === 'compare' && (
        <CompareView key={`compare-${sessionKey}`} c={C} onReset={reset} reportContext={reportContext} />
      )}
      {stage === 'benchmark' && (
        <BenchmarkView key={`benchmark-${sessionKey}`} c={C} tenureChoice="leasehold" onReset={reset} reportContext={reportContext} />
      )}
      </Box>
  );
}
